import { Navigate, type RouteObject } from "react-router-dom";
import { CROWN_MODULES } from "./components/crown-data-import/modules";
import { RequireAuth } from "./hooks/use-auth";
import ApiKeyAdminDetailPage from "./pages/api-keys-admin/api-key-admin-detail-page";
import ApiKeyPayloadMappingPage from "./pages/api-keys-admin/api-key-payload-mapping-page";
import ApiKeysAdminListPage from "./pages/api-keys-admin/api-keys-admin-list-page";
import DocumentationAdminPage from "./pages/documentation-admin/documentation-admin-page";
import OrganizationMessagesPage from "./pages/organization-messages/organization-messages-page";
import UserSessionsAdminPage from "./pages/user-sessions-admin/user-sessions-admin-page";
import AdhocIngestionPreviewBatchPage from "./pages/adhoc-ingestion-preview-batch-page";
import AdhocIngestionPreviewPage from "./pages/adhoc-ingestion-preview-page";
import BarcodeGeneratorPage from "./pages/barcode-generator-page";
import CodeGenPage from "./pages/code-gen-page";
import CompanyConfigurationsPage from "./pages/company-configurations/company-configurations-page";
import ConfigMigrationPage from "./pages/config-migration/config-migration-page";
import CrownDataImportPage from "./pages/crown-data-import/crown-data-import-page";
import DocExtractionEvalRunPage from "./pages/doc-extraction-eval-run-page";
import DocExtractionEvalsPage from "./pages/doc-extraction-evals";
import {
  DocsTabRedirect,
  SchemaRunRedirect,
  SchemaTestListRedirect,
} from "./pages/doc-ingestion/legacy-redirects";
import DocIngestionLogsPage from "./pages/doc-ingestion/logs-page";
import DocIngestionOutlookIntegrationsPage from "./pages/doc-ingestion/outlook-integrations-page";
import SchemaEditorPage from "./pages/doc-ingestion/schema-editor-page";
import SchemaListPage from "./pages/doc-ingestion/schema-list-page";
import SchemaTestListsPage from "./pages/doc-ingestion/schema-test-lists-page";
import DocIngestionStatisticsPage from "./pages/doc-ingestion/statistics-page";
import EdiPage from "./pages/edi/edi-page";
import ViewEdiMessagesPage from "./pages/edi/view/view-edi-messages-page";
import HomePage from "./pages/home-page";
import IngestionInspectorPage from "./pages/ingestion-inspector-page";
import IngestionTestRunDetailPage from "./pages/ingestion-test-run-detail-page";
import IngestionTestRunsPage from "./pages/ingestion-test-runs-page";
import InvoiceEmailsPage from "./pages/invoice-emails/invoice-emails-page";
import LoginPage from "./pages/login-page";
import NotificationCutoverPage from "./pages/notification-cutover/notification-cutover-page";
import NotificationShadowPage from "./pages/notification-shadow/notification-shadow-page";
import OrderMirrorPage from "./pages/order-mirror/order-mirror-page";
import PieceBackfillPage from "./pages/piece-backfill/piece-backfill-page";
import QueuesPage from "./pages/queues-page";
import RenderPage from "./pages/render-page";
import { SchemaRunPage } from "./pages/schema-run-page";
import { SchemaTestListPage } from "./pages/schema-test-list-page";
import SftpUploadsPage from "./pages/sftp-uploads-page";
import StorybookPage from "./pages/storybook";
import OutboundTestBenchPage from "./pages/outbound-test-bench/outbound-test-bench-page";
import TenediConnectionsPage from "./pages/tenedi-connections/tenedi-connections-page";
import TestIntegrationOrdersPage from "./pages/test-integration-orders/test-integration-orders-page";
import TestOrderEditPage from "./pages/test-orders/[uuid]/test-order-edit-page";
import TestOrderCreatePage from "./pages/test-orders/create/test-order-create-page";
import TestOrderPage from "./pages/test-orders/test-order-page";
import { ROUTES } from "./route-paths";

/**
 * Renders a Crown module's component as a JSX element.
 *
 * Assigns the component to a capitalized local so JSX treats it as a React
 * component rather than a host element when rendering from the module
 * registry.
 *
 * @param Component - The module's reusable importer/validator component.
 * @returns The rendered element for use as a route `element`.
 */
const renderCrownModule = (
  Component: (typeof CROWN_MODULES)[number]["component"],
) => <Component />;

// Every top-level `path` comes from `ROUTES`, which is what lets the home
// launcher's links be checked against the routes that actually exist.
export const ROUTER_CONFIG: RouteObject[] = [
  {
    path: ROUTES.ROOT,
    element: <LoginPage />,
  },
  {
    path: ROUTES.LOGIN,
    element: <LoginPage />,
  },
  {
    path: ROUTES.HOME,
    element: <HomePage />,
  },
  {
    path: ROUTES.DOC_INGESTION_SCHEMA_EDITOR,
    element: <SchemaEditorPage />,
  },
  {
    path: ROUTES.DOC_INGESTION_SCHEMA_EDITOR_DETAIL,
    element: <SchemaEditorPage />,
  },
  {
    path: ROUTES.DOC_INGESTION_SCHEMAS,
    element: <SchemaListPage />,
  },
  {
    path: ROUTES.DOC_INGESTION_OUTLOOK_INTEGRATIONS,
    element: <DocIngestionOutlookIntegrationsPage />,
  },
  {
    path: ROUTES.DOC_INGESTION_STATISTICS,
    element: <DocIngestionStatisticsPage />,
  },
  {
    path: ROUTES.DOC_INGESTION_LOGS,
    element: <DocIngestionLogsPage />,
  },
  {
    path: ROUTES.DOC_INGESTION_SCHEMA_TEST_LISTS,
    element: <SchemaTestListsPage />,
  },
  {
    path: ROUTES.DOC_INGESTION_SCHEMA_TEST_LIST_DETAIL,
    element: <SchemaTestListPage />,
  },
  {
    path: ROUTES.DOC_INGESTION_SCHEMA_RUN_DETAIL,
    element: <SchemaRunPage />,
  },
  {
    path: ROUTES.LEGACY_DOCS,
    element: <DocsTabRedirect />,
  },
  {
    path: ROUTES.LEGACY_SCHEMA_TEST_LISTS,
    element: <Navigate replace to={ROUTES.DOC_INGESTION_SCHEMA_TEST_LISTS} />,
  },
  {
    path: ROUTES.LEGACY_SCHEMA_TEST_LIST_DETAIL,
    element: <SchemaTestListRedirect />,
  },
  {
    path: ROUTES.LEGACY_SCHEMA_RUN_DETAIL,
    element: <SchemaRunRedirect />,
  },
  {
    path: ROUTES.DOC_EXTRACTION_EVALS,
    element: <DocExtractionEvalsPage />,
  },
  {
    path: ROUTES.DOC_EXTRACTION_EVAL_RUN,
    element: <DocExtractionEvalRunPage />,
  },
  {
    path: ROUTES.INGESTION_INSPECTOR,
    element: <IngestionInspectorPage />,
  },
  {
    path: ROUTES.ADHOC_INGESTION_PREVIEW,
    element: <AdhocIngestionPreviewPage />,
  },
  {
    path: ROUTES.ADHOC_INGESTION_PREVIEW_BATCH,
    element: <AdhocIngestionPreviewBatchPage />,
  },
  {
    path: ROUTES.INGESTION_TEST_RUNS,
    element: <IngestionTestRunsPage />,
  },
  {
    path: ROUTES.INGESTION_TEST_RUN_DETAIL,
    element: <IngestionTestRunDetailPage />,
  },
  {
    path: ROUTES.EDI,
    element: <EdiPage />,
  },
  {
    path: ROUTES.EDI_VIEW,
    element: <ViewEdiMessagesPage />,
  },
  {
    path: ROUTES.INVOICE_TRANSMISSIONS,
    element: <InvoiceEmailsPage />,
  },
  {
    path: ROUTES.CODE_GEN,
    element: <CodeGenPage />,
  },
  {
    path: ROUTES.BARCODE_GENERATOR,
    element: <BarcodeGeneratorPage />,
  },
  {
    path: ROUTES.RENDER,
    element: <RenderPage />,
  },
  {
    path: ROUTES.SFTP_UPLOADS,
    element: <SftpUploadsPage />,
  },
  {
    path: ROUTES.TEST_ORDERS,
    element: <TestOrderPage />,
  },
  {
    path: ROUTES.TEST_ORDER_CREATE,
    element: <TestOrderCreatePage />,
  },
  {
    path: ROUTES.TEST_ORDER_EDIT,
    element: <TestOrderEditPage />,
  },
  {
    path: ROUTES.COMPANY_CONFIGURATIONS,
    element: <CompanyConfigurationsPage />,
  },
  {
    path: ROUTES.CONFIG_MIGRATION,
    element: <ConfigMigrationPage />,
  },
  {
    path: ROUTES.NOTIFICATION_CUTOVER,
    element: <NotificationCutoverPage />,
  },
  {
    path: ROUTES.NOTIFICATION_SHADOW,
    element: <NotificationShadowPage />,
  },
  {
    path: ROUTES.PIECE_BACKFILL,
    element: <PieceBackfillPage />,
  },
  {
    path: ROUTES.CROWN_DATA_IMPORT,
    element: <CrownDataImportPage />,
    children: [
      { index: true, element: <Navigate replace to="tariff" /> },
      ...CROWN_MODULES.map((m) => ({
        path: m.path,
        element: renderCrownModule(m.component),
      })),
    ],
  },
  {
    path: ROUTES.CROWN_CHARGES_VALIDATOR,
    element: <Navigate replace to="/crown-data-import/charges-validator" />,
  },
  {
    path: ROUTES.CONTACTS_IMPORTER,
    element: <Navigate replace to="/crown-data-import/contacts" />,
  },
  {
    path: ROUTES.TARIFF_IMPORTER,
    element: <Navigate replace to="/crown-data-import/tariff" />,
  },
  {
    path: ROUTES.ACCESSORIAL_IMPORTER,
    element: <Navigate replace to="/crown-data-import/accessorial" />,
  },
  {
    path: ROUTES.ORDERS_IMPORTER,
    element: <Navigate replace to="/crown-data-import/orders" />,
  },
  {
    path: ROUTES.USERS_IMPORTER,
    element: <Navigate replace to="/crown-data-import/users" />,
  },
  {
    path: ROUTES.DRIVERS_IMPORTER,
    element: <Navigate replace to="/crown-data-import/drivers" />,
  },
  {
    path: ROUTES.STORYBOOK,
    element: <StorybookPage />,
  },
  {
    path: ROUTES.QUEUES,
    element: <QueuesPage />,
  },
  {
    path: ROUTES.API_KEYS_ADMIN,
    element: (
      <RequireAuth>
        <ApiKeysAdminListPage />
      </RequireAuth>
    ),
  },
  {
    path: ROUTES.ORGANIZATION_MESSAGES,
    element: (
      <RequireAuth>
        <OrganizationMessagesPage />
      </RequireAuth>
    ),
  },
  {
    path: ROUTES.DOCUMENTATION_ADMIN,
    element: (
      <RequireAuth>
        <DocumentationAdminPage />
      </RequireAuth>
    ),
  },
  {
    path: ROUTES.USER_SESSIONS_ADMIN,
    element: (
      <RequireAuth>
        <UserSessionsAdminPage />
      </RequireAuth>
    ),
  },
  {
    path: ROUTES.API_KEY_ADMIN_DETAIL,
    element: (
      <RequireAuth>
        <ApiKeyAdminDetailPage />
      </RequireAuth>
    ),
  },
  {
    path: ROUTES.API_KEY_PAYLOAD_MAPPING,
    element: (
      <RequireAuth>
        <ApiKeyPayloadMappingPage />
      </RequireAuth>
    ),
  },
  {
    path: ROUTES.ORDER_MIRROR,
    element: (
      <RequireAuth>
        <OrderMirrorPage />
      </RequireAuth>
    ),
  },
  {
    path: ROUTES.TEST_INTEGRATION_ORDERS,
    element: (
      <RequireAuth>
        <TestIntegrationOrdersPage />
      </RequireAuth>
    ),
  },
  {
    path: ROUTES.OUTBOUND_TEST_BENCH,
    element: (
      <RequireAuth>
        <OutboundTestBenchPage />
      </RequireAuth>
    ),
  },
  {
    path: ROUTES.TENEDI_CONNECTIONS,
    element: (
      <RequireAuth>
        <TenediConnectionsPage />
      </RequireAuth>
    ),
  },
];
